import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useId, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { claveServicios } from "@/componentes/catalogos/serviciosProveedor"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { tienePermiso } from "@/lib/autorizacion"
import { decimalOpcional } from "@/lib/decimales"
import { usarSesion } from "@/lib/sesion"

type Divisa = { id: number; codigo: string; nombre: string }

/** Un servicio de sus tipos, para elegirlo al agregar. */
export type Elegible = {
  gasto_id: number
  nombre: string
  tipo_proveedor: string | null
  monto_referencia: string | null
  divisa: string | null
}

const referencia = (s: { monto_referencia: string | null; divisa: string | null }) =>
  s.monto_referencia ? `${monto(s.monto_referencia)} ${s.divisa ?? ""}`.trim() : null

/**
 * Marcar que el proveedor presta un servicio de su tipo, con SU precio (04/11): `PUT
 * /proveedores/{id}/servicios/{gasto_id}`. Vale para agregarlo y para cambiarle el precio.
 *
 * ⚠ **El precio vacío NO es cero: es «el de referencia»**, el del servicio en su tipo. Así
 * una almacenadora sin tarifa propia sale con la de siempre y no con un 0 que nadie tecleó.
 *
 * ⚠ **Con `elegibles`, se elige cuál**, y uno que no está se CREA en su tipo principal
 * —«Almacenaje» en «Almacenadora»—, para que lo encuentren las demás almacenadoras.
 */
export function PrecioDelServicio({
  proveedorId,
  servicio,
  elegibles,
  tipoParaCrear,
  precioActual,
  onListo,
}: {
  proveedorId: number
  /** El servicio cuyo precio se cambia; sin él, se elige de `elegibles`. */
  servicio?: Elegible
  elegibles?: Elegible[]
  tipoParaCrear?: { id: number; nombre: string }
  precioActual?: { monto: string | null; divisa_id: number | null }
  onListo: () => void
}) {
  const id = useId()
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const [gastoId, setGastoId] = useState<number | null>(servicio?.gasto_id ?? null)
  const [creados, setCreados] = useState<Elegible[]>([])
  const [precio, setPrecio] = useState(precioActual?.monto ?? "")
  const [divisaId, setDivisaId] = useState<number | null>(precioActual?.divisa_id ?? null)

  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
    enabled: tienePermiso(usuario, "tesoreria:ver") || tienePermiso(usuario, "orden_pago:crear"),
  })

  const opciones = [...(elegibles ?? []), ...creados]
  const elegido = servicio ?? opciones.find((o) => o.gasto_id === gastoId)
  const montoLimpio = decimalOpcional(precio.trim() === "" ? null : precio.trim())

  const guardar = useMutation({
    mutationFn: () =>
      api.put(`/api/v1/proveedores/${proveedorId}/servicios/${gastoId}`, {
        monto: montoLimpio,
        divisa_id: montoLimpio === null ? null : divisaId,
      }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: claveServicios(proveedorId) })
      onListo()
    },
  })

  async function crearEnSuTipo(nombre: string): Promise<number> {
    const nuevo = await api.post<{ id: number }>("/api/v1/gastos", {
      nombre,
      tipo_proveedor_id: tipoParaCrear?.id,
      tipo_gasto: "VARIABLE",
    })
    setCreados((c) => [
      ...c,
      {
        gasto_id: nuevo.id,
        nombre,
        tipo_proveedor: tipoParaCrear?.nombre ?? null,
        monto_referencia: null,
        divisa: null,
      },
    ])
    await clienteQuery.invalidateQueries({ queryKey: ["gastos"] })
    return nuevo.id
  }

  const faltaMoneda = montoLimpio !== null && divisaId === null
  const ref = elegido ? referencia(elegido) : null

  return (
    <form
      aria-label={servicio ? `Precio de ${servicio.nombre}` : "Agregar servicio"}
      className="tarjeta space-y-3 p-3"
      onSubmit={(evento) => {
        evento.preventDefault()
        if (gastoId !== null && !faltaMoneda) guardar.mutate()
      }}
    >
      {!servicio && (
        <label className="block text-sm" htmlFor={`${id}-servicio`}>
          Servicio
          <SelectorBuscable
            id={`${id}-servicio`}
            opciones={opciones.map((o) => ({
              valor: o.gasto_id,
              etiqueta: o.nombre,
              detalle: [o.tipo_proveedor, referencia(o)].filter(Boolean).join(" · ") || undefined,
            }))}
            valor={gastoId}
            onCambiar={setGastoId}
            placeholder="Buscar en los servicios de su tipo…"
            queSeCrea={tipoParaCrear ? `servicio de ${tipoParaCrear.nombre}` : undefined}
            onCrear={
              tipoParaCrear && tienePermiso(usuario, "gasto:administrar")
                ? crearEnSuTipo
                : undefined
            }
          />
        </label>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm" htmlFor={`${id}-precio`}>
          Su precio
          <input
            id={`${id}-precio`}
            inputMode="decimal"
            className="campo mt-1 tabular-nums"
            value={precio}
            onChange={(e) => setPrecio(e.target.value)}
          />
          <span className="mt-0.5 block text-xs" style={{ color: "var(--fg-muted)" }}>
            {ref ? `Vacío: el de referencia, ${ref}` : "Vacío: sin precio"}
          </span>
        </label>
        <label className="block text-sm" htmlFor={`${id}-divisa`}>
          Moneda
          <select
            id={`${id}-divisa`}
            className="campo mt-1"
            value={divisaId ?? ""}
            onChange={(e) => setDivisaId(e.target.value === "" ? null : Number(e.target.value))}
          >
            <option value="">—</option>
            {divisas.data?.map((d) => (
              <option key={d.id} value={d.id}>
                {d.codigo}
              </option>
            ))}
          </select>
        </label>
      </div>

      {faltaMoneda && (
        <p className="text-xs" style={{ color: "var(--warning)" }}>
          Un precio necesita su moneda.
        </p>
      )}
      {guardar.error instanceof ErrorApi && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {guardar.error.message}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Boton
          type="submit"
          variante="primario"
          tamano="sm"
          disabled={gastoId === null || faltaMoneda || guardar.isPending}
        >
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </Boton>
        <Boton tamano="sm" onClick={onListo} disabled={guardar.isPending}>
          Cancelar
        </Boton>
      </div>
    </form>
  )
}
